import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowLeft, Play, RotateCcw, CheckCircle2, Brain, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const INITIAL_SPAN = 3;
const MAX_FAILURES = 2;
const DISPLAY_TIME_PER_DIGIT = 800; // ms per digit

function generateSequence(length) {
  return Array.from({ length }, () => Math.floor(Math.random() * 10));
}

export default function DigitSpanTask({ onBack }) {
  const [phase, setPhase] = useState('intro'); // intro, playing, showing, input, result
  const [taskType, setTaskType] = useState('forward');
  const [spanLength, setSpanLength] = useState(INITIAL_SPAN);
  const [currentSequence, setCurrentSequence] = useState([]);
  const [displayIndex, setDisplayIndex] = useState(-1);
  const [userInput, setUserInput] = useState('');
  const [trials, setTrials] = useState([]);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [maxSpan, setMaxSpan] = useState(0);
  const [result, setResult] = useState(null);
  const [trialFeedback, setTrialFeedback] = useState(null);
  const displayTimer = useRef(null);
  const inputRef = useRef(null);

  const startGame = useCallback((type) => {
    setTaskType(type);
    setSpanLength(INITIAL_SPAN);
    setTrials([]);
    setConsecutiveFailures(0);
    setMaxSpan(0);
    setResult(null);
    setTrialFeedback(null);
    setUserInput('');
    startNewTrial(INITIAL_SPAN, type);
  }, []);

  const startNewTrial = (length, type) => {
    const seq = generateSequence(length);
    setCurrentSequence(seq);
    setDisplayIndex(0);
    setUserInput('');
    setTrialFeedback(null);
    setPhase('showing');
  };

  // Display digits one at a time
  useEffect(() => {
    if (phase !== 'showing') return;
    if (displayIndex >= currentSequence.length) {
      // Done showing — move to input
      const timeout = setTimeout(() => {
        setDisplayIndex(-1);
        setPhase('input');
        setTimeout(() => inputRef.current?.focus(), 100);
      }, 300);
      return () => clearTimeout(timeout);
    }
    displayTimer.current = setTimeout(() => {
      setDisplayIndex(prev => prev + 1);
    }, DISPLAY_TIME_PER_DIGIT);
    return () => { if (displayTimer.current) clearTimeout(displayTimer.current); };
  }, [phase, displayIndex, currentSequence]);

  const handleSubmitAnswer = useCallback(() => {
    const digits = userInput.split('').map(Number).filter(n => !isNaN(n));
    let expected;
    if (taskType === 'forward') {
      expected = [...currentSequence];
    } else {
      expected = [...currentSequence].reverse();
    }
    const correct = JSON.stringify(digits) === JSON.stringify(expected);

    const trial = {
      sequence: currentSequence,
      user_input: digits,
      correct,
      span_length: spanLength,
    };
    const updated = [...trials, trial];
    setTrials(updated);

    if (correct) {
      setTrialFeedback('correct');
      const newMax = Math.max(maxSpan, spanLength);
      setMaxSpan(newMax);
      setConsecutiveFailures(0);
      setTimeout(() => {
        const newSpan = spanLength + 1;
        setSpanLength(newSpan);
        startNewTrial(newSpan, taskType);
      }, 800);
    } else {
      setTrialFeedback('incorrect');
      const newFails = consecutiveFailures + 1;
      setConsecutiveFailures(newFails);
      if (newFails >= MAX_FAILURES) {
        // Game over
        setTimeout(() => submitResults(updated, Math.max(maxSpan, spanLength - 1)), 800);
      } else {
        setTimeout(() => {
          startNewTrial(spanLength, taskType);
        }, 800);
      }
    }
    setUserInput('');
  }, [userInput, currentSequence, taskType, spanLength, trials, maxSpan, consecutiveFailures]);

  const submitResults = async (allTrials, finalMaxSpan) => {
    const correctCount = allTrials.filter(t => t.correct).length;
    const payload = {
      task_type: taskType,
      max_span: Math.max(finalMaxSpan, INITIAL_SPAN - 1),
      total_correct: correctCount,
      total_trials: allTrials.length,
      trials: allTrials,
    };
    try {
      const res = await axios.post(`${API}/api/cognitive/digit-span/submit`, payload);
      setResult(res.data);
      setPhase('result');
    } catch {
      setResult({ label: 'Hata', description: 'Sonuç kaydedilemedi.', color: 'red', max_span: finalMaxSpan });
      setPhase('result');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && userInput.length > 0) {
      handleSubmitAnswer();
    }
  };

  const colorMap = { emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' }, cyan: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' }, amber: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' }, orange: { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' }, red: { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' } };

  // INTRO
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-6">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-lg w-full" data-testid="digit-intro">
          <button onClick={onBack} className="flex items-center gap-2 text-white/50 hover:text-white/80 mb-6 transition" data-testid="digit-back">
            <ArrowLeft className="w-4 h-4" /> Geri Dön
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Sayı Dizisi Görevi</h1>
              <p className="text-white/50 text-sm">Bellek Kapasitesi Testi</p>
            </div>
          </div>
          <div className="space-y-3 mb-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-semibold text-sm mb-1 flex items-center gap-2"><Eye className="w-4 h-4 text-cyan-400" /> İleri — Kısa Süreli Bellek</h3>
              <p className="text-white/50 text-xs">Gösterilen sayıları <span className="text-cyan-300">aynı sırayla</span> girin. 3 basamaktan başlar, her doğru cevapta 1 artar. 2 ardışık hata oyunu bitirir.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-semibold text-sm mb-1 flex items-center gap-2"><EyeOff className="w-4 h-4 text-amber-400" /> Geri — Çalışma Belleği</h3>
              <p className="text-white/50 text-xs">Gösterilen sayıları <span className="text-amber-300">ters sırayla</span> girin. Belleğinizi daha zorlu bir şekilde test eder.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => startGame('forward')} className="flex-1 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2" data-testid="digit-start-forward">
              <Play className="w-4 h-4" /> İleri
            </button>
            <button onClick={() => startGame('backward')} className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2" data-testid="digit-start-backward">
              <Play className="w-4 h-4" /> Geri
            </button>
          </div>
        </div>
      </div>
    );
  }

  // SHOWING
  if (phase === 'showing') {
    const accentColor = taskType === 'forward' ? 'text-cyan-400' : 'text-amber-400';
    return (
      <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6" data-testid="digit-showing">
        <div className="text-center">
          <p className="text-white/40 text-sm mb-2">
            {taskType === 'forward' ? 'İleri Sayı Dizisi' : 'Geri Sayı Dizisi'} — {spanLength} basamak
          </p>
          <p className="text-white/30 text-xs mb-8">Sayıları hatırlayın</p>
          <div className={`text-7xl sm:text-8xl font-black ${accentColor} transition-all duration-200`}>
            {displayIndex >= 0 && displayIndex < currentSequence.length
              ? currentSequence[displayIndex]
              : ''}
          </div>
          <div className="flex gap-2 justify-center mt-8">
            {currentSequence.map((_, i) => (
              <div key={i} className={`w-3 h-3 rounded-full transition-all ${
                i < displayIndex ? 'bg-white/30' : i === displayIndex ? (taskType === 'forward' ? 'bg-cyan-400 scale-125' : 'bg-amber-400 scale-125') : 'bg-white/10'
              }`} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // INPUT
  if (phase === 'input') {
    const accentColor = taskType === 'forward' ? 'border-cyan-500/50 focus:border-cyan-400' : 'border-amber-500/50 focus:border-amber-400';
    return (
      <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6" data-testid="digit-input">
        <div className="w-full max-w-sm text-center">
          <p className="text-white/40 text-sm mb-2">{spanLength} basamak</p>
          <p className="text-white/60 text-lg mb-6">
            {taskType === 'forward'
              ? 'Sayıları aynı sırayla girin'
              : 'Sayıları ters sırayla girin'}
          </p>

          {trialFeedback && (
            <div className={`mb-4 py-2 rounded-lg text-sm font-medium ${
              trialFeedback === 'correct' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
            }`}>
              {trialFeedback === 'correct' ? 'Doğru!' : 'Yanlış'}
            </div>
          )}

          <input ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={userInput}
            onChange={e => setUserInput(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={handleKeyDown}
            placeholder="Sayıları girin..."
            className={`w-full text-center text-3xl font-mono tracking-[0.5em] py-4 bg-white/5 border ${accentColor} rounded-xl text-white placeholder:text-white/20 outline-none transition`}
            maxLength={spanLength + 2}
            autoFocus
            data-testid="digit-input-field"
          />
          <p className="text-white/30 text-xs mt-2">Hata hakkı: {MAX_FAILURES - consecutiveFailures} / {MAX_FAILURES}</p>

          <button onClick={handleSubmitAnswer}
            disabled={userInput.length === 0}
            className={`w-full mt-6 py-3 rounded-xl font-semibold transition ${
              userInput.length > 0
                ? (taskType === 'forward' ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-amber-500 hover:bg-amber-600') + ' text-white'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
            }`}
            data-testid="digit-submit-btn">
            Gönder
          </button>
        </div>
      </div>
    );
  }

  // RESULT
  if (phase === 'result' && result) {
    const c = colorMap[result.color] || colorMap.amber;
    const correctCount = trials.filter(t => t.correct).length;
    return (
      <div className="min-h-screen bg-[#0f172a] py-8 px-4" data-testid="digit-result">
        <div className="max-w-lg mx-auto space-y-6">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="text-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-white">
                Sayı Dizisi — {taskType === 'forward' ? 'İleri' : 'Geri'}
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className={`${c.bg} border ${c.border} rounded-xl p-4 text-center`}>
                <p className={`text-3xl font-bold ${c.text}`}>{result.max_span}</p>
                <p className="text-white/40 text-xs mt-1">Maks. Basamak</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{correctCount}/{trials.length}</p>
                <p className="text-white/40 text-xs mt-1">Doğru/Toplam</p>
              </div>
            </div>
            <div className="text-center mb-4">
              <span className={`${c.bg} ${c.text} px-4 py-1.5 rounded-full text-sm font-medium border ${c.border}`}>
                {result.label}
              </span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed text-center">{result.description}</p>
            {result.measures && <p className="text-white/30 text-xs text-center mt-3">Ölçülen: {result.measures}</p>}
          </div>
          <div className="flex gap-3">
            <button onClick={() => startGame(taskType)} className="flex-1 py-3 bg-amber-500/20 text-amber-300 rounded-xl font-medium transition hover:bg-amber-500/30 flex items-center justify-center gap-2" data-testid="digit-retry">
              <RotateCcw className="w-4 h-4" /> Tekrarla
            </button>
            <button onClick={() => setPhase('intro')} className="flex-1 py-3 bg-white/10 text-white/70 rounded-xl font-medium transition hover:bg-white/15" data-testid="digit-switch">
              Diğer Mod
            </button>
            <button onClick={onBack} className="flex-1 py-3 bg-white/10 text-white/70 rounded-xl font-medium transition hover:bg-white/15" data-testid="digit-done">
              Tamamla
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
